import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getAttendance } from '@/api/student';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { PageHeader, QueryState, Stat, Table } from '@/components/kit/ui';
import { unwrap } from '@/components/kit/data';
import { fmtDate } from '@/lib/format';

interface Att { month: string; days: number; present: number; late: number; absent: number; excused: number; rate: number | null; records: { date: string; status: string; note: string | null }[] }

const shift = (m: string, d: number) => { const [y, mo] = m.split('-').map(Number); const dt = new Date(y, mo - 1 + d, 1); return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`; };

export function AttendancePage() {
  const now = new Date().toISOString().slice(0, 7);
  const [month, setMonth] = useState(now);
  const q = useQuery({ queryKey: ['student-attendance', month], queryFn: () => getAttendance({ month }).then((r) => unwrap<Att>(r)) });
  const d = q.data;
  return (
    <div className="space-y-4">
      <PageHeader title="My attendance" subtitle="Daily attendance as marked by your class teacher." action={
        <div className="flex items-center gap-2">
          <Button variant="ghost" onClick={() => setMonth(shift(month, -1))}>Previous</Button>
          <span className="text-sm font-medium w-32 text-center">{new Date(`${month}-01`).toLocaleDateString('en-NG', { month: 'long', year: 'numeric' })}</span>
          <Button variant="ghost" disabled={month >= now} onClick={() => setMonth(shift(month, 1))}>Next</Button>
        </div>
      } />
      <QueryState q={q}>
        {d && (
          <>
            <div className="grid sm:grid-cols-5 gap-3">
              <Stat label="Rate" value={d.rate !== null ? `${d.rate}%` : '—'} />
              <Stat label="Present" value={d.present} />
              <Stat label="Late" value={d.late} />
              <Stat label="Absent" value={d.absent} tone={d.absent ? 'text-red-700' : undefined} />
              <Stat label="Excused" value={d.excused} />
            </div>
            <Card>
              <Table rows={d.records ?? []} rowKey={(r) => r.date} empty="No attendance recorded this month." cols={[
                { key: 'date', header: 'Date', render: (r) => fmtDate(r.date) },
                { key: 'status', header: 'Status', render: (r) => <StatusBadge status={r.status} /> },
                { key: 'note', header: 'Note', render: (r) => r.note ?? '' },
              ]} />
            </Card>
          </>
        )}
      </QueryState>
    </div>
  );
}
